// Cliente del upscaling de video (Topaz vía Replicate).
//
// Flujo async en 2 pasos (mismo patrón que omni/traducción, pero el molde de
// n8n es TalkNet: el START crea la prediction en Replicate y devuelve el
// prediction_id que Replicate generó; NO lo genera el cliente):
//   1) START → dispara la prediction y devuelve `prediction_id`.
//   2) STATUS → se polea hasta `done` (sube el mp4 a `video-project-assets`) o
//      `error`.
//
// El upscaling es LENTO (minutos), por eso el poll es 15s / ~20min de margen.
// Ver ADR-596 y docs/superpowers/specs/2026-07-23-upscale-video-design.md.
import { supabase } from './supabase';
import {
  GENERATE_AUTH_TOKEN,
  UPSCALE_VIDEO_START_URL,
  UPSCALE_VIDEO_STATUS_URL,
} from '../config';

export type UpscaleResolution = '720p' | '1080p' | '4k';
export type UpscaleStatus = 'processing' | 'done' | 'error';

export interface VideoUpscaleParams {
  userId: string;
  companyId: string;
  projectId: string;
  /** Id del clip/asset de origen (para trazabilidad en n8n). */
  clipId: string;
  /** Signed URL (descargable) del video a mejorar. */
  videoUrl: string;
  /** Duración del video en segundos (para el costo/logging). */
  durationSec: number;
  /** Resolución destino del modelo Topaz (define calidad y costo). */
  targetResolution: UpscaleResolution;
}

export interface VideoUpscaleResult {
  predictionId: string;
  /** Storage path del video mejorado dentro de `video-project-assets`. */
  path: string;
  /** Signed URL lista para reproducir el resultado. */
  signedUrl: string;
}

const BUCKET = 'video-project-assets';
const POLL_INTERVAL_MS = 15000;
const POLL_MAX_ATTEMPTS = 80; // ~20 min de margen (Topaz es lento)

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

/**
 * Dispara el upscaling y polea el status hasta que termina. Devuelve el path +
 * signed URL del video mejorado. Lanza Error si falla o si hace timeout.
 * `onStatus` reporta las transiciones (para la UI).
 */
export async function upscaleVideo(
  params: VideoUpscaleParams,
  onStatus?: (s: UpscaleStatus) => void,
): Promise<VideoUpscaleResult> {
  // ── 1) START ──────────────────────────────────────────────────────────
  const startRes = await fetch(UPSCALE_VIDEO_START_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
    },
    body: JSON.stringify({
      user_id: params.userId,
      company_id: params.companyId,
      project_id: params.projectId,
      clip_id: params.clipId,
      video_url: params.videoUrl,
      duration_sec: params.durationSec,
      target_resolution: params.targetResolution,
    }),
  });
  if (!startRes.ok) {
    throw new Error(`Error al iniciar el upscaling (${startRes.status}).`);
  }
  const startRaw = await startRes.text();
  let startJson: {
    prediction_id?: string;
    status?: string;
    message?: string;
    error?: string;
  };
  try {
    startJson = JSON.parse(startRaw);
  } catch {
    throw new Error('Respuesta inválida del servidor de upscaling.');
  }
  // El START puede fallar por créditos insuficientes u otro motivo: en ese caso
  // n8n devuelve status:"error" con message (no viene prediction_id).
  if (startJson.status === 'error' || !startJson.prediction_id) {
    throw new Error(
      startJson.message || startJson.error || 'No se pudo iniciar el upscaling.',
    );
  }
  const predictionId = startJson.prediction_id;
  onStatus?.('processing');

  // ── 2) POLL STATUS ────────────────────────────────────────────────────
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    await sleep(POLL_INTERVAL_MS);
    let st: { status?: string; path?: string; message?: string; error?: string };
    try {
      const stRes = await fetch(UPSCALE_VIDEO_STATUS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({
          prediction_id: predictionId,
          project_id: params.projectId,
          company_id: params.companyId,
        }),
      });
      if (!stRes.ok) continue; // reintentar el poll
      st = await stRes.json();
    } catch {
      continue; // error de red puntual → reintentar
    }

    if (st.status === 'done' && st.path) {
      const { data: urlData } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(st.path, 3600);
      if (!urlData?.signedUrl) {
        throw new Error('Video mejorado pero no se pudo obtener la URL.');
      }
      onStatus?.('done');
      return { predictionId, path: st.path, signedUrl: urlData.signedUrl };
    }
    if (st.status === 'error') {
      onStatus?.('error');
      throw new Error(st.message || st.error || 'El upscaling del video falló.');
    }
    // status === 'processing' → seguir poleando
  }

  throw new Error('El upscaling del video tardó demasiado. Reintentá.');
}
